import ReactModal from "react-modal";
import { X, ArrowRight, Calendar } from "lucide-react";
import dayjs from "dayjs";

import { TranslatedText } from "@/components/common/text";

export const DeleteFilterConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    filter,
    loading,
    error,
}) => {
    if (!filter) return null;

    const departure = filter.departure;
    const dropOff = filter.dropOff;
    // Extract date part (YYYY-MM-DD) to avoid timezone conversion issues
    const availableDate = filter.availableDateStart ? dayjs(filter.availableDateStart.slice(0, 10)) : null;
    const minOffer = filter.minOffer;

    const departureLabel = departure
        ? `${departure.city}, ${departure.state}`
        : "Unknown";

    const dropOffLabel = dropOff
        ? `${dropOff.city}, ${dropOff.state}`
        : "Any destination";

    const dateLabel = availableDate ? availableDate.format("MMM D") : null;

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface rounded-xl w-[480px] flex flex-col pointer-events-auto shadow-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between pl-5 pr-3 py-3 border-b border-cm-p-outline">
                    <h2 className="text-[18px] font-medium text-cm-p-text-primary tracking-tight">
                        <TranslatedText>Delete this filter?</TranslatedText>
                    </h2>
                    <button type="button" onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded hover:bg-cm-p-surface-hover">
                        <X className="w-5 h-5 text-cm-p-text-secondary" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-4 pt-5 pb-8 flex flex-col gap-3">
                    <p className="text-[14px] text-cm-p-text-secondary px-1">
                        <TranslatedText>Are you sure you want to delete this filter?</TranslatedText>
                    </p>

                    {/* Filter card */}
                    <div className="border-[1.5px] border-cm-p-outline rounded-lg px-3 py-2.5 flex flex-col gap-2">
                        {/* Route info */}
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <div className="flex items-center justify-center w-3 h-3">
                                    <div className="w-2 h-2 rounded bg-cm-p-on-container-green" />
                                </div>
                                <span className="text-[14px] font-medium text-cm-p-text-primary">
                                    {departureLabel}
                                </span>
                            </div>

                            <ArrowRight className="w-4 h-4 text-cm-p-text-secondary" />

                            <span className={`text-[14px] ${dropOff ? "font-medium text-cm-p-text-primary" : "text-cm-p-text-secondary"}`}>
                                {dropOffLabel}
                            </span>
                        </div>

                        {/* Tags */}
                        <div className="flex items-center gap-2">
                            {dateLabel && (
                                <div className="flex items-center gap-1.5 h-[20px] px-2 rounded bg-cm-p-surface-container">
                                    <Calendar className="w-3 h-3 text-cm-p-text-primary" />
                                    <span className="text-[12px] font-medium text-cm-p-text-primary">
                                        {dateLabel}
                                    </span>
                                </div>
                            )}
                            {minOffer != null && (
                                <div className="flex items-center justify-center h-[20px] px-2 rounded bg-cm-p-surface-container">
                                    <span className="text-[12px] font-medium text-cm-p-text-primary">
                                        $ {minOffer.toLocaleString()} +
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="px-1 py-2 text-[13px] text-[#e25353]">
                            <TranslatedText>{typeof error === "string" ? error : "Failed to delete filter"}</TranslatedText>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end items-center gap-[10px] px-4 py-4 border-t border-cm-p-outline">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-[120px] h-[44px] rounded-md bg-cm-p-surface-container text-cm-p-text-primary text-[14px] font-medium focus:outline-none"
                    >
                        <TranslatedText>Cancel</TranslatedText>
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className="w-[130px] h-[44px] rounded-md bg-[#e25353] text-white text-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d04545]"
                    >
                        <TranslatedText>Yes, delete</TranslatedText>
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};
